import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Users, Clock, CheckCircle2 } from 'lucide-react';
function GoldenSparkle({
  delay,
  x,
  y,
  size = 8
}: {
  delay: number;
  x: string;
  y: string;
  size?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0
  }} animate={{
    opacity: [0, 1, 0.8, 0],
    scale: [0, 1.2, 0.8, 0]
  }} transition={{
    duration: 1.8,
    delay,
    ease: 'easeOut'
  }} className="absolute rounded-full pointer-events-none" style={{
    left: x,
    top: y,
    width: size,
    height: size,
    background: 'radial-gradient(circle, #F5C518 0%, #FFD700 40%, transparent 70%)',
    boxShadow: `0 0 ${size * 2}px ${size}px rgba(245, 197, 24, 0.6), 0 0 ${size * 4}px ${size * 2}px rgba(255, 215, 0, 0.3)`
  }} data-id="element-121" />;
}
function GoldenRay({
  delay,
  rotation,
  length
}: {
  delay: number;
  rotation: number;
  length: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    scaleY: 0
  }} animate={{
    opacity: [0, 0.7, 0],
    scaleY: [0, 1, 0.5]
  }} transition={{
    duration: 1.5,
    delay,
    ease: 'easeOut'
  }} className="absolute left-1/2 top-1/2 pointer-events-none origin-bottom" style={{
    width: 2,
    height: length,
    background: 'linear-gradient(to top, rgba(245, 197, 24, 0.8), transparent)',
    transform: `translate(-50%, -100%) rotate(${rotation}deg)`
  }} data-id="element-122" />;
}
function AnimatedCounter({
  target,
  duration = 2000
}: {
  target: number;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <>{count.toLocaleString('es-AR')}</>;
}
export function HeroSection() {
  return <section className="min-h-screen md:min-h-0 flex flex-col px-4 md:pt-20 md:pb-24 max-w-5xl mx-auto text-center relative pt-[0px] pb-[0px] pl-[13px] pr-[13px] mt-[0px] mb-[0px] items-center justify-center" data-id="element-123">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-yellow/10 blur-[120px] rounded-full -z-10 pointer-events-none" data-id="element-124" />

      {/* Trust bar */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className="flex items-center justify-center gap-2 md:gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 md:mb-4 pt-[4px] pb-[4px] pl-[8px] pr-[8px] mt-[4px] mb-[4px] ml-[0px] mr-[0px] overflow-x-auto no-scrollbar whitespace-nowrap" data-id="element-125">
        <span className="flex items-center gap-1 text-gray-400 text-[10px] md:text-xs font-medium" data-id="element-126">
          <Users className="w-3 h-3 text-gray-400" data-id="element-127" />
          <span data-id="element-128">
            +<AnimatedCounter target={3000} duration={2000} data-id="element-129" /> alumnos
          </span>
        </span>
        <div className="w-px h-2 bg-white/10" data-id="element-130" />
        <span className="flex items-center gap-1 text-gray-400 text-[10px] md:text-xs font-medium" data-id="element-131">
          <Clock className="w-3 h-3 text-gray-400" data-id="element-132" />
          <span data-id="element-133">Acceso inmediato</span>
        </span>
        <div className="w-px h-2 bg-white/10" data-id="element-134" />
        <span className="flex items-center gap-1 text-gray-400 text-[10px] md:text-xs font-medium" data-id="element-135">
          <CheckCircle2 className="w-3 h-3 text-gray-400" data-id="element-136" />
          <span data-id="element-137">Garantía 7 días</span>
        </span>
      </motion.div>

      <motion.h1 initial={{
      opacity: 0,
      y: -30,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }} className="text-[22px] md:text-7xl font-black leading-[1.1] px-2 md:px-0 tracking-tight pt-[12px] pb-[12px]" data-id="element-138">
        <span className="text-white" data-id="element-139">NECESITAS UN </span>
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5C518] via-[#FFD700] to-[#F5C518]" data-id="element-140">
          CAMINO CLARO
        </span>
        <br data-id="element-141" />
        <span className="text-white" data-id="element-142">PARA EMPEZAR EN </span>
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#F5C518] to-[#FFD700]" data-id="element-143">
          DETAILING
        </span>
      </motion.h1>

      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.2
    }} className="mb-4 md:mb-10 relative my-2 md:my-0" data-id="element-144">
        {/* Golden glow behind book */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.2,
        delay: 0.4
      }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-145">
          <div className="w-[340px] h-[340px] md:w-[500px] md:h-[500px] rounded-full" style={{
          background: 'radial-gradient(circle, rgba(245, 197, 24, 0.3) 0%, rgba(255, 215, 0, 0.12) 40%, transparent 70%)',
          filter: 'blur(50px)'
        }} data-id="element-146" />
        </motion.div>

        {/* Golden rays */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-147">
          <div className="relative w-0 h-0" data-id="element-148">
            <GoldenRay delay={0.5} rotation={0} length={180} data-id="element-149" />
            <GoldenRay delay={0.6} rotation={45} length={150} data-id="element-150" />
            <GoldenRay delay={0.7} rotation={90} length={170} data-id="element-151" />
            <GoldenRay delay={0.55} rotation={135} length={140} data-id="element-152" />
            <GoldenRay delay={0.65} rotation={180} length={160} data-id="element-153" />
            <GoldenRay delay={0.75} rotation={225} length={145} data-id="element-154" />
            <GoldenRay delay={0.8} rotation={270} length={175} data-id="element-155" />
            <GoldenRay delay={0.6} rotation={315} length={155} data-id="element-156" />
          </div>
        </div>

        <a href="#compra" onClick={e => {
        e.preventDefault();
        document.querySelector('#compra')?.scrollIntoView({
          behavior: 'smooth'
        });
      }} data-id="element-157">
          <img src="https://cdn.magicpatterns.com/uploads/2VLg5AL25uhhxxhZ6DwZ4U/Libro_Principal.png" alt="Formación en Detallado Automotor" className="w-full max-w-[320px] md:max-w-xl mx-auto drop-shadow-[0_25px_60px_rgba(245,197,24,0.35)] hover:scale-105 transition-transform duration-500 cursor-pointer px-4 md:px-8 relative z-10" style={{
          filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.15))'
        }} data-id="element-158" />
        </a>
      </motion.div>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.6,
      delay: 0.6
    }} className="md:text-2xl text-gray-300 font-medium max-w-3xl mx-auto text-[14px] md:text-[16px] mb-3 md:mb-0 px-2 md:px-0" data-id="element-159">
        Aprendé paso a paso cómo arrancar desde cero y convertirlo en ingresos
        reales.
      </motion.p>

      <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.5,
      delay: 0.8
    }} className="mt-2 md:mt-0" data-id="element-160">
        <Button data-id="element-161">QUIERO EMPEZAR AHORA</Button>
      </motion.div>
    </section>;
}